import type React from 'react';
import { useEffect, useState } from 'react';

import {
  THRESHOLD,
  THRESHOLD_CARD_LINKS_ENABLED,
} from '@xing-com/crate-dream-machine-constants';

type ActionType = 'left' | 'right' | 'super';

import { useSearchAndShuffleContext } from './context';

type UseSwipeableCardProps = {
  handleSwipe: (deckIndex: number, action: ActionType) => void;
  deckIndex: number;
  action?: ActionType;
};

type UseSwipeableCardReturn = {
  handlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  swipeDirection: ActionType | undefined;
  isSwiping: boolean;
  currentPos: { x: number; y: number };
  activeSwipeDirection: ActionType | undefined;
};

const getActiveSwipeDirection = (
  isSwiping: boolean,
  currentPos: { x: number; y: number },
  action: ActionType | undefined
): ActionType | undefined => {
  if (isSwiping && Math.abs(currentPos.x) > THRESHOLD) {
    return currentPos.x > 0 ? 'right' : 'left';
  }
  return action;
};

export const useSwipeableCard = ({
  handleSwipe,
  action,
  deckIndex,
}: UseSwipeableCardProps): UseSwipeableCardReturn => {
  const { cardLinksEnabled, isModalEnabled } = useSearchAndShuffleContext();
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  // This state is updated when the user starts to swipe
  const [isSwiping, setIsSwiping] = useState(false);
  // This state is updated when the action is defined
  const [swipeAction, setSwipeAction] = useState<ActionType>();
  const swipeDirection = swipeAction;

  const activeSwipeDirection = getActiveSwipeDirection(
    isSwiping,
    currentPos,
    swipeAction
  );

  // Set the swipe action if props action is updated
  useEffect(() => {
    if (action) {
      setSwipeAction(action);
    }
  }, [action]);

  useEffect(() => {
    if (swipeAction) {
      handleSwipe(deckIndex, swipeAction);
    }
  }, [swipeAction]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    const target = e.target as Element;
    if (isModalEnabled || (e.pointerType === 'mouse' && e.button !== 0)) {
      return;
    }

    target.setPointerCapture(e.pointerId); // Capture pointer events
    setStartPos({ x: e.clientX, y: e.clientY });
    setIsSwiping(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!isSwiping) return;
    const dx = e.clientX - startPos.x;
    const dy = e.clientY - startPos.y;
    // if (Math.abs(dx) > THRESHOLD_ACTION) {
    //   if (dx > 0) {
    //     setSwipeAction('right');
    //   } else {
    //     setSwipeAction('left');
    //   }
    // } else {
    setCurrentPos({ x: dx, y: dy });
    //}

    cardLinksEnabled.current = Math.abs(dx) < THRESHOLD_CARD_LINKS_ENABLED;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const target = e.target as Element;
    if (isSwiping && !swipeAction) {
      const dx = currentPos.x;
      if (Math.abs(dx) > THRESHOLD) {
        if (dx > 0) {
          setSwipeAction('right');
        } else {
          setSwipeAction('left');
        }
      } else {
        // Reset
        setIsSwiping(false);
        setCurrentPos({ x: 0, y: 0 });
        // setTimeout to wait for the click events to be processed after the click is released
        setTimeout(() => (cardLinksEnabled.current = true), 0);
      }
    }

    target.releasePointerCapture(e.pointerId); // Release pointer capture
  };
  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    swipeDirection,
    isSwiping,
    currentPos,
    activeSwipeDirection,
  };
};

export default useSwipeableCard;
